import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sp3dl8o1vn02ekujb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'company_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '亚洲天堂',
    alternateName: 'Asia Paradise',
    url: 'https://asiaparadise.com/',
    description: '亚洲天堂——探索东方最美旅游胜地，汇聚东南亚、东亚及南亚最令人心动的绝美目的地。',
    email: 'contact@asiaparadise.com',
    telephone: '+86-400-888-9999',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressRegion: '上海',
      addressCountry: 'CN',
    },
    foundingDate: '2022',
    sameAs: [],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于亚洲天堂 - 探索东方最美旅游胜地',
    description: '了解亚洲天堂的品牌故事、口碑评价和联系方式。亚洲天堂汇聚亚洲顶级旅游资源，为您打造一站式梦幻旅行体验。',
    url: 'https://asiaparadise.com/about',
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-2">
              关于亚洲天堂
            </h1>
            <p className="text-foreground-600 text-sm md:text-base mt-3 max-w-2xl">
              用心发现亚洲之美，为每一位旅行者点亮梦想之旅
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-5">
                  让每一次出发都值得期待
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">亚洲天堂</strong>创立于2022年，是一支由资深旅行者和内容创作者组成的团队。我们热爱亚洲这片神奇的土地，从热带雨林到雪域高原，从千年古都到摩登都市，我们用脚步丈量每一寸土地，用文字记录每一次感动。
                  </p>
                  <p>
                    我们的使命是为中文旅行者提供<strong className="text-foreground-900">最真实、最实用、最深入</strong>的亚洲旅行资讯。每一篇文章都经过实地探访和精心撰写，每一个推荐都来自亲身经历。
                  </p>
                  <p>
                    目前亚洲天堂已覆盖<strong className="text-foreground-900">东南亚、东亚、南亚</strong>三大区域超过20个目的地，累计发布旅行攻略500+篇，服务超过100万旅行爱好者。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">500+</div>
                    <div className="text-xs text-foreground-500 mt-1">旅行攻略</div>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-accent-600">20+</div>
                    <div className="text-xs text-foreground-500 mt-1">目的地</div>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-secondary-600">100万+</div>
                    <div className="text-xs text-foreground-500 mt-1">旅行爱好者</div>
                  </div>
                </div>
              </div>

              <div className="relative w-full h-[320px] md:h-[420px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20group%20of%20young%20Asian%20travelers%20sitting%20together%20in%20a%20cozy%20modern%20office%20with%20large%20world%20map%20on%20wall%2C%20warm%20natural%20light%20from%20large%20windows%2C%20laptop%20and%20travel%20photos%20on%20wooden%20table%2C%20friendly%20creative%20atmosphere%2C%20professional%20lifestyle%20photography%2C%20warm%20tones%2C%20clean%20composition&width=800&height=600&seq=about-team-image&orientation=landscape"
                  alt="亚洲天堂团队"
                  title="亚洲天堂团队 - 用心发现亚洲之美"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-xs font-medium text-secondary-600 bg-secondary-100/80 px-3 py-1 rounded-full mb-3">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
                旅行者的真实声音
              </h2>
              <p className="text-foreground-600 text-sm mt-2">
                来自亚洲天堂读者的真诚反馈
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.location} · {t.tripDestination}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100/70 px-3 py-1 rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-5">
                  期待与您相遇
                </h2>
                <p className="text-foreground-600 text-sm md:text-base mb-8">
                  无论是旅行咨询、商务合作还是内容投稿，我们都期待听到您的声音。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">邮箱</div>
                      <div className="text-sm text-foreground-600">contact@asiaparadise.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">电话</div>
                      <div className="text-sm text-foreground-600">+86 400-888-9999</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">地址</div>
                      <div className="text-sm text-foreground-600">上海市浦东新区陆家嘴金融区</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/60">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                  发送消息
                </h3>
                <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-secondary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-secondary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-secondary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    />
                  </div>

                  <div className="form-anti-spam">
                    <label htmlFor="company_alt">Company</label>
                    <input type="text" id="company_alt" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-sm text-accent-600 bg-accent-50 border border-accent-200 rounded-md px-4 py-3 text-center">
                      消息已发送！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-sm text-secondary-600 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3 text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}